/**
 * R90 Product Enhancements
 * - Image filtering by variant color (alt text matching)
 * - Staggered fade-in animation for product media
 * - Dynamic model info update on variant change
 * - Size calculator modal logic
 */

(function () {
  'use strict';

  // ========================================================================
  // IMAGE FILTERING BY VARIANT COLOR
  // ========================================================================

  function getSelectedColor() {
    // Try multiple selectors — Horizon uses variant-picker with fieldsets
    const selectedSwatch = document.querySelector(
      'variant-picker fieldset:first-of-type input[type="radio"]:checked,' +
      'variant-picker fieldset:first-of-type .variant-button[aria-checked="true"],' +
      'variant-picker .variant-swatch[aria-checked="true"]'
    );

    if (selectedSwatch) {
      return (selectedSwatch.value || selectedSwatch.getAttribute('aria-label') || '').trim();
    }

    // Fallback: look for the first option label
    const selectedButton = document.querySelector(
      'variant-picker fieldset:first-of-type [aria-checked="true"],' +
      'variant-picker fieldset:first-of-type input:checked'
    );

    if (selectedButton) {
      const label = selectedButton.closest('label') || selectedButton.nextElementSibling;
      if (label) return label.textContent.trim();
      return (selectedButton.value || '').trim();
    }

    // Last fallback: check URL params
    const params = new URLSearchParams(window.location.search);
    for (const [key, value] of params) {
      if (key.toLowerCase().includes('color') || key.toLowerCase().includes('colour')) {
        return value;
      }
    }

    return null;
  }

  function normalizeAlt(alt) {
    if (!alt) return '';
    return alt.toLowerCase().replace(/[_\-]/g, ' ').trim();
  }

  function filterGalleryImages(color) {
    const grid = document.querySelector('.media-gallery__grid');
    if (!grid) return;

    const items = grid.querySelectorAll(':scope > li');
    if (!items.length) return;

    const normalizedColor = normalizeAlt(color);
    let visibleIndex = 0;

    items.forEach(function (item) {
      const img = item.querySelector('img');
      if (!img) {
        // Non-image media — always show
        item.classList.remove('r90-hidden');
        item.classList.add('r90-visible');
        item.style.setProperty('--r90-stagger-delay', visibleIndex * 150 + 'ms');
        visibleIndex++;
        return;
      }

      const imgAlt = normalizeAlt(img.getAttribute('alt'));

      // Show if: alt matches the selected color, alt is empty (generic), or no color selected
      if (!normalizedColor || imgAlt === normalizedColor || imgAlt === '') {
        item.classList.remove('r90-hidden');
        item.style.setProperty('--r90-stagger-delay', (visibleIndex * 150) + 'ms');
        visibleIndex++;

        // Trigger staggered fade-in
        requestAnimationFrame(function () {
          item.classList.add('r90-visible');
        });
      } else {
        item.classList.add('r90-hidden');
        item.classList.remove('r90-visible');
      }
    });
  }

  function filterMobileGallery(color) {
    var gallery = document.querySelector('.r90-mobile-gallery');
    if (!gallery) return;

    var slides = gallery.querySelectorAll('.r90-mobile-swipe-slide');
    var thumbnails = gallery.querySelectorAll('.r90-mobile-thumbnail');
    if (!slides.length || !thumbnails.length) return;

    var normalizedColor = normalizeAlt(color);
    var firstMatchIndex = null;
    var visibleCount = 0;

    for (var i = 0; i < thumbnails.length; i++) {
      var thumb = thumbnails[i];
      var slide = slides[i];
      var thumbAlt = normalizeAlt(thumb.getAttribute('data-alt'));

      // Show if: alt matches the selected color, alt is empty (generic), or no color selected
      if (!normalizedColor || thumbAlt === normalizedColor || thumbAlt === '') {
        thumb.style.display = '';
        if(slide) slide.style.display = '';
        visibleCount++;
        
        // Track first exact color match
        if (firstMatchIndex === null && normalizedColor && thumbAlt === normalizedColor) {
          firstMatchIndex = i;
        }
      } else {
        thumb.style.display = 'none';
        thumb.classList.remove('r90-thumb-active');
        if(slide) slide.style.display = 'none';
      }
    }

    // Fallback: if nothing matched, show all
    if (visibleCount === 0) {
      for (var j = 0; j < thumbnails.length; j++) {
        thumbnails[j].style.display = '';
        if(slides[j]) slides[j].style.display = '';
      }
      return;
    }

    // Auto-scroll slider to first matching slide horizontally without scrolling window vertically
    if (firstMatchIndex !== null) {
      for (var k = 0; k < thumbnails.length; k++) {
        thumbnails[k].classList.remove('r90-thumb-active');
      }
      thumbnails[firstMatchIndex].classList.add('r90-thumb-active');
      
      var slide = slides[firstMatchIndex];
      var slider = gallery.querySelector('.r90-mobile-swipe-slider');
      if (slide && slider) {
        var targetLeft = slide.getBoundingClientRect().left - slider.getBoundingClientRect().left + slider.scrollLeft;
        slider.scrollTo({
          left: targetLeft,
          behavior: 'auto'
        });
      }
    }
  }

  function runStaggeredEntrance() {
    const grid = document.querySelector('.media-gallery__grid');
    if (!grid) return;

    const items = grid.querySelectorAll(':scope > li:not(.r90-hidden)');
    items.forEach(function (item, index) {
      item.style.setProperty('--r90-stagger-delay', (index * 150) + 'ms');
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          item.classList.add('r90-visible');
        });
      });
    });
  }

  // ========================================================================
  // DYNAMIC MODEL INFO
  // ========================================================================

  function updateModelInfo() {
    var container = document.getElementById('r90-model-info');
    var dataEl = document.getElementById('r90-color-model-map');
    if (!container || !dataEl) return;

    var modelMap;
    try {
      modelMap = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }

    // Get the currently selected color from the variant picker
    var color = getSelectedColor();
    var normalizedColor = color ? color.toLowerCase().trim() : '';

    // Look up in the color model map; fall back to _default
    var text = modelMap[normalizedColor] || modelMap['_default'] || '';

    if (text) {
      container.textContent = text;
      container.style.opacity = '1';
    } else {
      container.textContent = '';
    }
  }

  // ========================================================================
  // SIZE CALCULATOR
  // ========================================================================

  function initSizeCalculator() {
    const trigger = document.getElementById('r90-size-calc-trigger');
    const backdrop = document.getElementById('r90-size-calc-modal');
    if (!trigger || !backdrop) return;

    const closeBtn = backdrop.querySelector('.r90-modal__close');
    const form = backdrop.querySelector('.r90-size-calc-form');
    const heightInput = backdrop.querySelector('#r90-height');
    const weightInput = backdrop.querySelector('#r90-weight');
    const resultBox = backdrop.querySelector('.r90-modal__result');
    const resultSize = backdrop.querySelector('.r90-modal__result-size');

    function openModal() {
      backdrop.classList.add('r90-active');
      document.body.style.overflow = 'hidden';
    }

    function closeModal() {
      backdrop.classList.remove('r90-active');
      document.body.style.overflow = '';
    }

    trigger.addEventListener('click', openModal);
    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', function (e) {
      if (e.target === backdrop) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && backdrop.classList.contains('r90-active')) {
        closeModal();
      }
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var height = parseFloat(heightInput.value);
      var weight = parseFloat(weightInput.value);

      if (isNaN(height) || isNaN(weight) || height <= 0 || weight <= 0) return;

      var size = calculateSize(height, weight);

      resultSize.textContent = size;
      resultBox.classList.add('r90-active');
    });
  }

  function calculateSize(height, weight) {
    // Weight-dominant base size
    var weightSize;
    if (weight < 65) {
      weightSize = 'M';
    } else if (weight <= 75) {
      weightSize = 'M';
    } else if (weight <= 85) {
      weightSize = 'L';
    } else {
      weightSize = 'XL';
    }

    // Height override
    var heightSize;
    if (height < 170) {
      heightSize = 'M';
    } else if (height <= 180) {
      heightSize = 'M';
    } else if (height <= 185) {
      heightSize = 'L';
    } else {
      heightSize = 'XL';
    }

    // Size ranking for comparison
    var sizeRank = { 'M': 1, 'L': 2, 'XL': 3 };

    // Rule: Weight dictates base. If height suggests LARGER, use the larger.
    if (sizeRank[heightSize] > sizeRank[weightSize]) {
      return heightSize;
    }
    return weightSize;
  }

  // ========================================================================
  // MOBILE GALLERY — SWIPE SLIDER & THUMBNAILS (R90 CUSTOM)
  // ========================================================================

  function isMobileViewport() {
    return window.innerWidth < 750;
  }

  function initMobileGallery() {
    var gallery = document.querySelector('.r90-mobile-gallery');
    if (!gallery) return;

    // Prevent duplicate event listeners if initialized multiple times (e.g. on morph)
    if (gallery.dataset.r90Initialized === 'true') return;
    gallery.dataset.r90Initialized = 'true';

    var slider = gallery.querySelector('.r90-mobile-swipe-slider');
    var thumbnails = gallery.querySelectorAll('.r90-mobile-thumbnail');
    var slides = gallery.querySelectorAll('.r90-mobile-swipe-slide');

    if (!slider || !thumbnails.length || !slides.length) return;

    // 1. Sync thumbnails on scroll of the slider (horizontal snap synchronization)
    var isScrolling;
    slider.addEventListener('scroll', function () {
      window.clearTimeout(isScrolling);
      isScrolling = setTimeout(function () {
        var scrollLeft = slider.scrollLeft;
        var width = slider.clientWidth;
        if (width <= 0) return;
        var index = Math.round(scrollLeft / width);
        
        // Only consider visible slides (for when variants filter images)
        var visibleSlides = Array.from(gallery.querySelectorAll('.r90-mobile-swipe-slide')).filter(function (s) {
          return s.style.display !== 'none';
        });

        if (visibleSlides[index]) {
          var realIndex = visibleSlides[index].getAttribute('data-index');
          thumbnails.forEach(function (t) {
            t.classList.remove('r90-thumb-active');
          });
          var targetThumb = gallery.querySelector('.r90-mobile-thumbnail[data-index="' + realIndex + '"]');
          if (targetThumb) {
            targetThumb.classList.add('r90-thumb-active');
          }
        }
      }, 50);
    }, { passive: true });

    // 2. Click handler on thumbnails to scroll horizontally without vertical page jump
    thumbnails.forEach(function (thumb, idx) {
      thumb.addEventListener('click', function (e) {
        e.preventDefault();
        
        var targetSlide = slides[idx];
        if (targetSlide && slider) {
          // Calculate horizontal offset relative to the slider container
          var targetLeft = targetSlide.getBoundingClientRect().left - slider.getBoundingClientRect().left + slider.scrollLeft;
          
          slider.scrollTo({
            left: targetLeft,
            behavior: 'smooth'
          });

          // Sync active class immediately
          thumbnails.forEach(function (t) {
            t.classList.remove('r90-thumb-active');
          });
          thumb.classList.add('r90-thumb-active');
        }
      });
    });

    // 3. Preload all slider images to ensure smooth transitions
    for (var p = 0; p < slides.length; p++) {
      var slideImg = slides[p].querySelector('img');
      var preloadSrc = slideImg ? slideImg.getAttribute('src') : null;
      if (preloadSrc) {
        var prefetch = new Image();
        prefetch.src = preloadSrc;
      }
    }
  }

  // ========================================================================
  // VARIANT CHANGE LISTENER
  // ========================================================================

  function onVariantChange() {
    var color = getSelectedColor();
    if (color) {
      filterGalleryImages(color);
      filterMobileGallery(color);
    }
    updateModelInfo();
  }

  // ========================================================================
  // DESKTOP: MUTATIONOBSERVER FOR GALLERY DOM REPLACEMENT
  // ========================================================================
  //
  // Horizon's <media-gallery> handles variant:update by doing
  //   this.replaceWith(newMediaGallery)
  // which nukes the old DOM (and all our r90-* classes with it).
  // A MutationObserver on the parent .shopify-section detects when a
  // fresh <media-gallery> is inserted and re-applies filtering + entrance.
  // ========================================================================

  function setupMediaGalleryObserver() {
    var mediaGallery = document.querySelector('media-gallery');
    var section = mediaGallery ? mediaGallery.closest('.shopify-section') : null;
    if (!section) return;

    var observer = new MutationObserver(function (mutations) {
      var galleryReplaced = false;

      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType === 1) {
            if (
              node.nodeName === 'MEDIA-GALLERY' ||
              (node.querySelector && node.querySelector('media-gallery'))
            ) {
              galleryReplaced = true;
              break;
            }
          }
        }
        if (galleryReplaced) break;
      }

      if (galleryReplaced) {
        // New <media-gallery> was inserted — re-apply filtering on fresh DOM
        requestAnimationFrame(function () {
          var color = getSelectedColor();
          if (color) {
            filterGalleryImages(color);
          }
          runStaggeredEntrance();
        });
      }
    });

    observer.observe(section, { childList: true, subtree: true });
  }

  // ========================================================================
  // INIT
  // ========================================================================

  function init() {
    // Run staggered entrance on page load
    runStaggeredEntrance();

    // Update model info on load
    updateModelInfo();

    // Init size calculator
    initSizeCalculator();

    // Init mobile gallery logic
    initMobileGallery();

    // --- DESKTOP: Watch for <media-gallery> DOM replacement ---
    setupMediaGalleryObserver();

    // --- MOBILE + MODEL INFO: Immediate pre-filter on swatch change ---
    // The mobile gallery (.r90-mobile-gallery) is NOT replaced by Horizon's
    // variant:update handler, so we can filter it right on the change event.
    document.addEventListener('change', function (e) {
      var target = e.target;
      if (
        target.closest('variant-picker') ||
        target.closest('[data-variant-input]') ||
        target.name === 'id'
      ) {
        var color = getSelectedColor();
        if (color) {
          filterMobileGallery(color);
        }
        updateModelInfo();
      }
    });

    // --- MOBILE + MODEL INFO: Also re-filter after server response ---
    // variant:update fires after Horizon fetches the updated section HTML.
    // We attach this directly to the document to survive section morphs/combined listings.
    document.addEventListener('variant:update', function () {
      // Re-initialize mobile gallery if the section was morphed/replaced
      initMobileGallery();

      // Double-rAF ensures all synchronous DOM work from other handlers
      // (including media-gallery replaceWith) has completed.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var color = getSelectedColor();
          if (color) {
            filterMobileGallery(color);
          }
          updateModelInfo();
        });
      });
    });

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', function () {
      setTimeout(onVariantChange, 100);
    });

    // Initial filter based on current variant selection
    var initialColor = getSelectedColor();
    if (initialColor) {
      filterGalleryImages(initialColor);
      filterMobileGallery(initialColor);
    }

    // Re-init mobile gallery on resize (e.g., rotate device)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        initMobileGallery();
      }, 250);
    });
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
